/**
 * Dashboard API Service
 * Handles all dashboard-related API operations
 * Migrated from legacy TabControl/MenuApi.js
 */

import { apiService } from './api';
import type {
  GetTasksCalendarRequest,
  CalendarEvent,
  GetTasksCalendarResponse,
} from '@/components/CalendarView/types';
import type { PersonFile } from './homeService';

// GridSize bounds accepted by the backend
export const MIN_GRID_SIZE = 5;
export const GRID_SIZE_UNIT_PX = 50;
export const MIN_COLUMN_WIDTH_PX = MIN_GRID_SIZE * GRID_SIZE_UNIT_PX;

export function clampGridSize(gridSize: number): number
{
  if (!Number.isFinite(gridSize)) return MIN_GRID_SIZE;
  return Math.max(MIN_GRID_SIZE, Math.round(gridSize));
}

export function toApiGridSize(columnWidthPx: number): number
{
  return clampGridSize(columnWidthPx / GRID_SIZE_UNIT_PX);
}

export function gridSizeToColumnWidth(gridSize: number): number
{
  return clampGridSize(gridSize) * GRID_SIZE_UNIT_PX;
}

// Dashboard Group from API response
export interface DashboardGroupResponse {
  DashboardGroupId: number;
  DashboardId: number;
  Name: string;
  IsDefault: boolean;
  SizeX: number;
  SizeY: number;
  Row: number;
  Col: number;
  showAllDetails: boolean;
  Conversations: null;
}

// Types
export interface Dashboard {
  DashboardId: number;
  Name: string;
  Order: number;
  IsDefault: boolean;
  IsPublic: boolean;
  IsTabPinedDashboard: boolean;
  IsPinedDashboard?: boolean;
  IsPineableDashboard?: boolean;
  IsOpenDashboard?: boolean;
  FakeDashboardType: number;
  CreatedById: number;
  PersonId: number;
  ProjectId?: number | null;
  ReportId?: number;
  StartDate?: string | null;
  DueDate?: string | null;
  showDeleteDashboard?: boolean;
  showEditName?: boolean;
  IsDashboardCreator?: boolean;
  showSubMenu?: boolean;
  id?: number;
  draftName?: string;
  NoShowData?: boolean;
  // Additional fields from API
  Archived?: boolean;
  ConfigValueId?: number;
  Filters?: string;
  GridSize?: number;
  ShowCardView?: boolean;
  DashboardViewTypeId?: number;
  DashboardGroups?: DashboardGroupResponse[];
  DashboardGroupId?: number;
  ConversationsDuplicate?: boolean;
  HasConversationsParents?: boolean;
  CalendarViewType?: number;
  DashboardLayerId?: number;
  EnableWeekend?: boolean;
}

export interface UpdatePositionRequest {
  dashboards: Dashboard[];
  isFromProject: boolean;
  noNotification: boolean;
  socketConnectionId: string;
}

export interface AddDashboardDefaultConfigRequest {
  ConfigValueId: number;
  ProjectId?: number;
  Order: number;
}

export interface CreateDashboardRequest {
  DashboardId: 0;
  Name: string;
  IsPublic: boolean;
  Archived: boolean;
  PersonId: string;
  ConfigValueId: number;
  ProjectId: number | null;
  GridSize: number;
  DashboardViewTypeId: number;
  Order: number;
  FakeDashboardType: number;
}

export interface DashboardEfficiencyRequest {
  DashboardId: number;
  StartDate: string;
  DueDate: string;
  Today: string;
  Name: string;
  WebUrl: string;
}

export interface RecycleBinConversation {
  ConversationId: number;
  Title: string;
  ProjectName: string | null;
  Icon: string | null;
  ConfigValueId: number | null;
  ConversationLayers: Array<{ LayerColor: string; LayerName: string }> | null;
  IsEnable: boolean;
}

export interface RecycleBinGroup {
  DashboardGroupId: number;
  DashboardId: number;
  Name: string;
  Conversations: RecycleBinConversation[];
}

export interface RecycleBinRequest {
  CustomFields: unknown[];
  PageNumber: number;
  PageSize: number;
  DashboardId: number;
  Filters: string;
  LayersToHide: string;
}

export interface GetGalleryFileProjectRequest {
  CustomFields: any[];
  Option: number;
  PageNumber: number;
  PageSize: number;
  DashboardId: number;
  Filters: string;
  SearchText: string;
  LayersToHide: string;
  typeFilter: number;
  Project: string;
  [key: string]: any;
}

class DashboardApiService
{
  /**
   * Get dashboards by config value id
   */
  async getByConfigValueId(
    configValueId: number,
    projectId?: number,
    organizeDashboard?: boolean,
  ): Promise<Dashboard[]>
  {
    let url = `dashboard/getbyconfigvalueid?configValueId=${configValueId}`;

    if (organizeDashboard)
    {
      url = 'dashboard/GetDashboadsGTD';
    }
    else if (projectId !== undefined && projectId !== null && projectId !== 0)
    {
      url = `${url}&projectId=${projectId}`;
    }

    return apiService.get<Dashboard[]>(url);
  }

  /**
   * Update dashboard (rename, etc.)
   */
  async updateDashboard(dashboard: Dashboard): Promise<Dashboard>
  {
    return apiService.put<Dashboard>('dashboard/updatedashboard', dashboard);
  }

  /**
   * Update dashboard positions
   */
  async updatePosition(request: UpdatePositionRequest): Promise<void>
  {
    return apiService.post<void>('dashboard/UpdatePosition', request);
  }

  /**
   * Create a new dashboard (GTD/Organize)
   */
  async create(request: CreateDashboardRequest): Promise<Dashboard>
  {
    return apiService.post<Dashboard>('dashboard/', request);
  }

  /**
   * Add dashboard with default configuration
   */
  async addDashboardDefaultConfiguration(request: AddDashboardDefaultConfigRequest): Promise<void>
  {
    return apiService.post<void>('dashboard/addDashboardDefaultConfiguration', request);
  }

  /**
   * Get dashboard by ID
   */
  async getDashboardById(dashboardId: number): Promise<Dashboard>
  {
    return apiService.get<Dashboard>(`dashboard/?id=${dashboardId}`);
  }

  /**
   * Get project status
   */
  async getProjectStatus(projectId: number): Promise<any[]>
  {
    return apiService.get<any[]>(`Status/GetProjectStatus?projectId=${projectId}`);
  }

  /**
   * Get recycle bin conversations
   */
  async getRecycleConversations(request: RecycleBinRequest): Promise<RecycleBinGroup[]>
  {
    return apiService.post<RecycleBinGroup[]>('conversation/GetRecycleConversations', request);
  }

  /**
   * Update dashboard view type and grid size
   * PUT dashboard/updatedashboardview
   */
  async updateDashboardView(
    dashboardId: number,
    gridSize: number,
    dashboardViewTypeId: number,
  ): Promise<void>
  {
    return apiService.put<void>('dashboard/updatedashboardview', {
      DashboardId: dashboardId,
      GridSize: clampGridSize(gridSize),
      DashboardViewTypeId: dashboardViewTypeId,
    });
  }

  /**
   * Get gallery files for a project/dashboard
   * POST conversation/GetGalleryFileProject
   */
  async getGalleryFileProject(request: GetGalleryFileProjectRequest): Promise<PersonFile[]>
  {
    return apiService.post<PersonFile[]>('conversation/GetGalleryFileProject', request);
  }

  /**
   * Get calendar tasks for a date range
   * POST conversation/GetTasksCalendar
   */
  async getTasksCalendar(params: GetTasksCalendarRequest): Promise<CalendarEvent[]>
  {
    const response = await apiService.post<GetTasksCalendarResponse>(
      'conversation/GetTasksCalendar',
      params,
    );
    return response?.Events || [];
  }

  /**
   * Export dashboard efficiency report
   */
  async reportDashboardEfficiency(request: DashboardEfficiencyRequest): Promise<Blob>
  {
    const response = await fetch(`${this.getBaseUrl()}api/Notification/ReportDashboardEfficiency`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.getToken()}`,
      },
      body: JSON.stringify(request),
    });
    return response.blob();
  }

  private getBaseUrl(): string
  {
    // Get from localStorage or config
    return localStorage.getItem('C2_apiURL') || '';
  }

  private getToken(): string
  {
    return localStorage.getItem('C2_access_token') || localStorage.getItem('access_token') || '';
  }
}

export const dashboardApi = new DashboardApiService();
export default dashboardApi;
